// paypal-fee.js
// add indonesian currency, update descriptions, and implement live exchange rates for accurate fee calculation in any currency
import React, { useState, useEffect } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "Kalkulator PayPal - Hitung Biaya dan Komisi PayPal dengan Akurat dan Cepat";
const DESCRIPTION =
  "Kalkulator PayPal gratis: hitung biaya dan komisi PayPal saat mengirim dan menerima, dan ketahui jumlah bersih atau total setelah potongan. Mendukung semua mata uang dan jenis transaksi.";
const KEYWORDS =
  "kalkulator PayPal, biaya dan komisi PayPal, hitung komisi PayPal, biaya PayPal, PayPal fee calculator, kalkulator biaya dan komisi PayPal, berapa biaya PayPal";
const OG_URL = "https://do-calculate.com/calculator/id/paypal-fee/";

const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Kalkulator PayPal",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "UtilityApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Beranda",
        item: "https://do-calculate.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Kalkulator",
        item: "https://do-calculate.com/id/all-calculators/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Kalkulator PayPal",
        item: OG_URL,
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Berapa biaya PayPal untuk transaksi komersial?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Biaya standar untuk transaksi komersial melalui PayPal adalah 3.49% + biaya tetap sesuai mata uang. Untuk transaksi dalam dolar AS, biaya tetapnya adalah 0.49 dolar. Persentase dan biaya tetap bervariasi tergantung jenis transaksi dan mata uang yang digunakan.",
        },
      },
      {
        "@type": "Question",
        name: "Apakah biaya PayPal gratis antar teman dan keluarga?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Di dalam negara yang sama, mengirim uang melalui opsi teman dan keluarga (Personal) gratis jika sumbernya adalah akun PayPal atau rekening bank. Namun jika menggunakan kartu kredit, biaya tambahan 2.9% akan dikenakan. Untuk transfer internasional, biaya konversi mata uang berlaku.",
        },
      },
      {
        "@type": "Question",
        name: "Bagaimana cara menghitung jumlah yang harus saya kirim agar penerima menerima jumlah tertentu setelah biaya?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Jika Anda ingin penerima menerima 100 dolar setelah dipotong biaya PayPal komersial (3.49% + 0.49 dolar), Anda harus mengirim sekitar 104.12 dolar. Gunakan kalkulator kami dan pilih 'Saya ingin penerima menerima jumlah penuh' dan masukkan 100 dolar untuk mendapatkan jumlah yang harus dikirim.",
        },
      },
    ],
  },
];

// Fixed fees in USD — will be converted dynamically via live exchange rates
// Source: PayPal fee schedule (USD base values)
const FIXED_FEES_USD = {
  standard:    { rate: 0.0349, fixedUSD: 0.49 },
  checkout:    { rate: 0.0299, fixedUSD: 0.49 },
  micropayment:{ rate: 0.05,   fixedUSD: 0.05 },
  friends:     { rate: 0.0,    fixedUSD: 0.00 },
  international:{ rate: 0.045, fixedUSD: 0.49 },
};

const TRANSACTION_TYPES = {
  standard:     { name: "Transaksi Komersial Standar (Standard)" },
  checkout:     { name: "PayPal Checkout" },
  micropayment: { name: "Pembayaran Kecil (Micropayment)" },
  friends:      { name: "Teman & Keluarga (Friends & Family)" },
  international:{ name: "Transfer Internasional (International)" },
};

// All currencies: Arabic-world countries first, then major global
const CURRENCIES = {
  // ── Negara Arab ──────────────────────────────
  SAR: { name: "Riyal Saudi — Arab Saudi (SAR)", symbol: "ر.س" },
  AED: { name: "Dirham UAE — Uni Emirat Arab (AED)", symbol: "د.إ" },
  KWD: { name: "Dinar Kuwait — Kuwait (KWD)", symbol: "د.ك" },
  BHD: { name: "Dinar Bahrain — Bahrain (BHD)", symbol: "د.ب" },
  OMR: { name: "Riyal Oman — Oman (OMR)", symbol: "ر.ع" },
  QAR: { name: "Riyal Qatar — Qatar (QAR)", symbol: "ر.ق" },
  JOD: { name: "Dinar Yordania — Yordania (JOD)", symbol: "د.أ" },
  EGP: { name: "Pound Mesir — Mesir (EGP)", symbol: "ج.م" },
  IQD: { name: "Dinar Irak — Irak (IQD)", symbol: "د.ع" },
  LYD: { name: "Dinar Libya — Libya (LYD)", symbol: "د.ل" },
  TND: { name: "Dinar Tunisia — Tunisia (TND)", symbol: "د.ت" },
  DZD: { name: "Dinar Aljazair — Aljazair (DZD)", symbol: "د.ج" },
  MAD: { name: "Dirham Maroko — Maroko (MAD)", symbol: "د.م" },
  SDG: { name: "Pound Sudan — Sudan (SDG)", symbol: "ج.س" },
  SYP: { name: "Lira Suriah — Suriah (SYP)", symbol: "ل.س" },
  LBP: { name: "Lira Lebanon — Lebanon (LBP)", symbol: "ل.ل" },
  YER: { name: "Riyal Yaman — Yaman (YER)", symbol: "ر.ي" },
  // ── Mata uang global utama ──────────────────────
  USD: { name: "Dolar AS (USD)", symbol: "$" },
  EUR: { name: "Euro (EUR)", symbol: "€" },
  GBP: { name: "Pound Sterling (GBP)", symbol: "£" },
  CAD: { name: "Dolar Kanada (CAD)", symbol: "C$" },
  AUD: { name: "Dolar Australia (AUD)", symbol: "A$" },
  JPY: { name: "Yen Jepang (JPY)", symbol: "¥" },
  CNY: { name: "Yuan Tiongkok (CNY)", symbol: "¥" },
  INR: { name: "Rupee India (INR)", symbol: "₹" },
  TRY: { name: "Lira Turki (TRY)", symbol: "₺" },
  IDR: { name: "Rupiah Indonesia (IDR)", symbol: "Rp" },
};

const CALC_MODE = {
  sender: "Saya ingin mengirim sejumlah uang (berapa yang diterima penerima?)",
  receiver: "Saya ingin penerima menerima jumlah tertentu (berapa yang harus saya kirim?)",
};

function PaypalCalculator() {
  const [amount, setAmount] = useState("");
  const [transactionType, setTransactionType] = useState("standard");
  const [currency, setCurrency] = useState("SAR");
  const [calcMode, setCalcMode] = useState("sender");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  // exchangeRates: always keyed from USD base { SAR: 3.75, AED: 3.67, ... }
  const [exchangeRates, setExchangeRates] = useState(null);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [ratesError, setRatesError] = useState(false);

  // Fetch rates using USD as base — cross-rate formula converts to any currency
  const fetchRates = () => {
    setRatesLoading(true);
    setRatesError(false);
    fetch("https://v6.exchangerate-api.com/v6/c215274af0ae2a4e2f63743a/latest/USD")
      .then((res) => res.json())
      .then((data) => {
        if (data && data.conversion_rates) {
          setExchangeRates(data.conversion_rates);
        } else {
          setRatesError(true);
        }
        setRatesLoading(false);
      })
      .catch(() => {
        setRatesLoading(false);
        setRatesError(true);
      });
  };

  useEffect(() => { fetchRates(); }, []);

  const calculateResult = () => {
    try {
      setError("");
      const amountNum = parseFloat(amount);
      if (!amountNum || amountNum <= 0) {
        setResult(null);
        return;
      }

      const txConfig = FIXED_FEES_USD[transactionType];
      const rate = txConfig.rate;

      // Convert the USD fixed fee to the selected currency using live cross-rates
      // Cross-rate: fixedInCurrency = fixedUSD * rates[currency]
      let fixedFee = txConfig.fixedUSD;
      let usdRate = 1;
      if (exchangeRates && currency !== "USD") {
        usdRate = exchangeRates[currency] || 1;
        fixedFee = txConfig.fixedUSD * usdRate;
      }

      let feePaid, amountSent, amountReceived;

      if (calcMode === "sender") {
        amountSent = amountNum;
        feePaid = amountSent * rate + fixedFee;
        amountReceived = amountSent - feePaid;
      } else {
        // sent = (received + fixed) / (1 - rate)
        amountReceived = amountNum;
        amountSent = (amountReceived + fixedFee) / (1 - rate);
        feePaid = amountSent - amountReceived;
      }

      if (amountReceived < 0) {
        setError("Jumlah kurang dari biaya tetap! Harap masukkan jumlah yang lebih besar.");
        setResult(null);
        return;
      }

      const feePercentage = (feePaid / amountSent) * 100;
      // Always compute USD equivalent of the fee
      const feeInUSD = currency === "USD" ? feePaid : feePaid / usdRate;

      setResult({
        amountSent,
        amountReceived,
        feePaid,
        feePercentage,
        fixedFee,
        fixedFeeUSD: txConfig.fixedUSD,
        feeInUSD,
        ratePercent: rate * 100,
        transactionName: TRANSACTION_TYPES[transactionType].name,
        currencySymbol: CURRENCIES[currency].symbol,
        currencyCode: currency,
        usdRate,
        mode: calcMode,
      });
    } catch (err) {
      setError("Kesalahan dalam perhitungan. Harap periksa data yang dimasukkan.");
      setResult(null);
    }
  };

  useEffect(() => {
    if (amount) {
      calculateResult();
    }
  }, [amount, transactionType, currency, calcMode]);

  const handleClear = () => {
    setAmount("");
    setTransactionType("standard");
    setCurrency("SAR");
    setCalcMode("sender");
    setResult(null);
    setError("");
  };

  const fmt = (num, decimals = 2) =>
    num !== undefined && num !== null ? num.toFixed(decimals) : "—";

  return (
    <div className="ltr" >
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={SCHEMA}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ padding: "15px", borderRadius: "5px" }}
          >
            <h1 style={{ fontSize: "22px", margin: 0 }}>Kalkulator PayPal</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            {/* Introduction */}
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                <strong>Kalkulator PayPal</strong> adalah alat gratis dan akurat yang membantu Anda <strong>menghitung biaya PayPal</strong> untuk transaksi apa pun, baik sebagai pengirim maupun penerima. Masukkan jumlah, tentukan jenis transaksi, dan dapatkan langsung biaya dan jumlah bersih. Ideal untuk penjual, freelancer, dan pemilik toko online.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />
            {/* Calculator Section */}
            <div className="calculatorStyle mb-4">
              <div className="p-3 mb-3 bg-success text-white text-center rounded">
                <h2 style={{ fontSize: "18px", margin: 0 }}>
                  Hitung Biaya PayPal dengan Mudah
                </h2>
              </div>

              {/* Calc Mode */}
              <div className="row">
                <div className="col-sm-12">
                  <label htmlFor="calcMode" >
                    Apa yang ingin Anda hitung?
                  </label>
                  <select
                    id="calcMode"
                    className="form-select"
                    value={calcMode}
                    onChange={(e) => setCalcMode(e.target.value)}
                  >
                    {Object.entries(CALC_MODE).map(([key, val]) => (
                      <option key={key} value={key}>
                        {val}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Transaction Type */}
              <div className="row">
                <div className="col-sm-12 col-md-6">
                  <label htmlFor="transactionType" >
                    Jenis Transaksi
                  </label>
                  <select
                    id="transactionType"
                    className="form-select"
                    value={transactionType}
                    onChange={(e) => setTransactionType(e.target.value)}
                  >
                    {Object.entries(TRANSACTION_TYPES).map(([key, { name }]) => (
                      <option key={key} value={key}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-sm-12 col-md-6">
                  <label htmlFor="currency" >
                    Mata Uang
                  </label>
                  <select
                    id="currency"
                    className="form-select"
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                  >
                    {Object.entries(CURRENCIES).map(([code, { name }]) => (
                      <option key={code} value={code}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Amount Input */}
              <div className="row">
                <div className="col-sm-12 col-md-6">
                  <label htmlFor="amount" >
                    {calcMode === "sender"
                      ? `Jumlah yang akan Anda kirim (${CURRENCIES[currency].symbol})`
                      : `Jumlah yang harus diterima penerima (${CURRENCIES[currency].symbol})`}
                  </label>
                  <input
                    id="amount"
                    type="number"
                    className="form-control"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                  />
                </div>
              </div>

              {/* Exchange Rate Info */}
              {ratesLoading && (
                <div className="alert alert-info mt-2" style={{ fontSize: "14px" }}>
                  ⏳ Sedang memuat nilai tukar mata uang langsung...
                </div>
              )}
              {ratesError && (
                <div className="alert alert-warning mt-2" style={{ fontSize: "14px" }}>
                  ⚠️ Gagal memuat nilai tukar.{" "}
                  <span
                    onClick={fetchRates}
                    style={{ cursor: "pointer", textDecoration: "underline", fontWeight: "bold" }}
                  >
                    Coba lagi
                  </span>
                </div>
              )}
              {exchangeRates && !ratesLoading && currency !== "USD" && (
                <div className="alert alert-info mt-2" style={{ fontSize: "14px" }}>
                  💱 Nilai tukar saat ini: 1 USD = {(exchangeRates[currency] || 0).toFixed(4)} {currency}
                </div>
              )}

              {/* Buttons */}
              <div className="text-center mb-3 mt-4">
                <button
                  onClick={handleClear}
                  className="btn btn-secondary"
                  style={{
                    fontSize: "16px",
                    padding: "10px 30px",
                  }}
                >
                  Hapus
                </button>
              </div>

              {/* Error */}
              {error && (
                <div className="alert alert-warning" role="alert">
                  <strong>Peringatan:</strong> {error}
                </div>
              )}

              {/* Results */}
              {result && (
                <div className="mt-4">
                  <h3
                    style={{
                      fontSize: "18px",
                      marginBottom: "15px",
                      textAlign: "center",
                    }}
                  >
                    <strong>Hasil:</strong>
                  </h3>
                  {/* Main Results Table */}
                  <div className="mb-3">
                    <table className="table bmi-table" style={{ width: "100%" }}>
                      <thead>
                        <tr>
                          <th className="bmi-header" style={{ textAlign: "center" }}>
                            Keterangan
                          </th>
                          <th className="bmi-header" style={{ textAlign: "center" }}>
                            Nilai
                          </th>
                         </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ textAlign: "center" }}>Jumlah yang Dikirim</td>
                          <td
                            style={{
                              textAlign: "center",
                              fontSize: "18px",
                              fontWeight: "bold",
                              color: "#1a5f3f",
                            }}
                          >
                            {fmt(result.amountSent)} {result.currencySymbol}
                          </td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>Biaya PayPal</td>
                          <td
                            style={{
                              textAlign: "center",
                              fontSize: "18px",
                              fontWeight: "bold",
                              color: "#c0392b",
                            }}
                          >
                            {fmt(result.feePaid)} {result.currencySymbol}
                          </td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>Jumlah Bersih (diterima penerima)</td>
                          <td
                            style={{
                              textAlign: "center",
                              fontSize: "18px",
                              fontWeight: "bold",
                              color: "#0f3d28",
                            }}
                          >
                            {fmt(result.amountReceived)} {result.currencySymbol}
                          </td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>Persentase Biaya Aktual</td>
                          <td style={{ textAlign: "center", fontSize: "16px" }}>
                            {fmt(result.feePercentage)}%
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="info-sections-container">
                    <div className="info-section">
                      <div className="info-section-header">
                        <strong>Detail Biaya</strong>
                      </div>
                      <div className="info-section-body">
                        <ul style={{ listStyle: "none", padding: 0, lineHeight: "2" }}>
                          <li>
                            💸 <strong>Biaya Persentase:</strong>{" "}
                            {result.ratePercent}% × {fmt(result.amountSent)} {result.currencySymbol} = {fmt(result.amountSent * result.ratePercent / 100)} {result.currencySymbol}
                          </li>
                          <li>
                            🔒 <strong>Biaya Tetap:</strong>{" "}
                            {fmt(result.fixedFee)} {result.currencySymbol}
                            {result.currencyCode !== "USD" && (
                              <span style={{ color: "#888", fontSize: "13px" }}>
                                {" "}(≈ ${fmt(result.fixedFeeUSD)} dengan nilai tukar saat ini)
                              </span>
                            )}
                          </li>
                          <li>
                            💰 <strong>Total Biaya:</strong>{" "}
                            {fmt(result.feePaid)} {result.currencySymbol}
                          </li>
                          {result.currencyCode !== "USD" && (
                            <li>
                              🌐 <strong>Biaya dalam Dolar (perkiraan):</strong>{" "}
                              ${fmt(result.feeInUSD)}
                            </li>
                          )}
                          {result.currencyCode !== "USD" && (
                            <li>
                              📊 <strong>Nilai Tukar yang Digunakan:</strong>{" "}
                              1 USD = {fmt(result.usdRate, 4)} {result.currencyCode}
                            </li>
                          )}
                        </ul>
                        <div
                          className="alert alert-info mt-3"
                          style={{ fontSize: "14px" }}
                        >
                          <strong>💡 Jenis Transaksi:</strong> {result.transactionName}
                        </div>
                      </div>
                    </div>
                    <div className="info-section">
                      <div className="info-section-header">
                        <strong>Detail Perhitungan</strong>
                      </div>
                      <div className="info-section-body">
                        <div style={{ lineHeight: "1.8" }}>
                          <p>
                            <strong>Metode Perhitungan:</strong>
                          </p>
                          <p style={{ fontSize: "14px", color: "#444" }}>
                            Biaya = (Jumlah × {result.ratePercent}%) + {fmt(result.fixedFee)} {result.currencySymbol}
                          </p>
                          <p style={{ fontSize: "14px", color: "#444" }}>
                            = ({fmt(result.amountSent)} × {result.ratePercent}%) + {fmt(result.fixedFee)}
                          </p>
                          <p style={{ fontSize: "14px", color: "#444" }}>
                            = {fmt(result.amountSent * result.ratePercent / 100)} + {fmt(result.fixedFee)} = {fmt(result.feePaid)} {result.currencySymbol}
                          </p>
                          <hr />
                          <p style={{ fontSize: "14px", color: "#666" }}>
                            <strong>Jumlah Bersih:</strong>{" "}
                            {fmt(result.amountSent)} - {fmt(result.feePaid)} = {fmt(result.amountReceived)} {result.currencySymbol}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <hr />
            {/* Related Links */}
            <div className="row">
              <RelatedLinks />
            </div>
          </div>
        </div>
        <div className="col-sm-12 col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>

      {/* Informational Content */}
      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">
                <strong>Apa itu Kalkulator PayPal?</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Kalkulator PayPal</strong> adalah alat elektronik gratis yang menghitung biaya PayPal secara akurat untuk jumlah dan mata uang apa pun. Baik Anda penjual online maupun freelancer yang menerima pembayaran dari luar negeri, Anda akan tahu dalam hitungan detik berapa yang akan dipotong dan berapa yang diterima pihak lain.
                </p>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  Cara Menggunakan Kalkulator:
                </h3>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>Pilih apakah Anda akan mengirim sejumlah uang atau ingin penerima menerima jumlah tertentu</li>
                  <li>Tentukan jenis transaksi (komersial, pribadi, internasional)</li>
                  <li>Pilih mata uang yang sesuai dari daftar</li>
                  <li>Masukkan jumlah dan hasil akan muncul langsung</li>
                  <li>Lihat detail biaya persentase dan biaya tetap di bagian detail</li>
                </ol>
                <div
                  className="alert alert-info mt-3"
                  style={{ lineHeight: "1.8" }}
                >
                  <strong>💡 Catatan:</strong> Kalkulator mengambil nilai tukar langsung secara otomatis untuk menampilkan nilai biaya dalam dolar AS saat menggunakan mata uang lain, membantu Anda memahami biaya sebenarnya.
                </div>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>Penggunaan Kalkulator PayPal</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  Untuk Freelancer dan Pekerja Lepas:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>Menentukan Harga Jasa:</strong> Hitung berapa yang harus Anda minta agar jumlah yang diterima utuh
                  </li>
                  <li>
                    <strong>Membuat Faktur:</strong> Tambahkan biaya PayPal ke faktur klien dengan transparan
                  </li>
                  <li>
                    <strong>Membandingkan Metode Pembayaran:</strong> Bandingkan biaya PayPal dengan alternatif lain
                  </li>
                  <li>
                    <strong>Menghitung Pendapatan Bersih:</strong> Ketahui berapa yang akan Anda terima setelah biaya sebelum menyelesaikan transaksi
                  </li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>
                  Untuk Toko Online:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>Menentukan Margin Keuntungan:</strong> Kurangkan biaya PayPal dari perhitungan sebelum menentukan harga
                  </li>
                  <li>
                    <strong>Analisis Biaya:</strong> Hitung biaya sebulan penuh berdasarkan penjualan Anda
                  </li>
                  <li>
                    <strong>Keputusan Strategis:</strong> Tentukan kapan PayPal yang terbaik dan kapan beralih ke alternatif
                  </li>
                  <li>
                    <strong>Negosiasi dengan Klien:</strong> Jelaskan kepada klien biaya pembayaran melalui PayPal
                  </li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>
                  Untuk Transfer Pribadi:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>Mengirim Uang:</strong> Ketahui berapa yang diterima teman atau keluarga di luar negeri
                  </li>
                  <li>
                    <strong>Membagi Pengeluaran:</strong> Hitung persis berapa yang harus dibayar setiap orang termasuk biaya
                  </li>
                  <li>
                    <strong>Transfer Internasional:</strong> Bandingkan biaya PayPal dengan transfer bank
                  </li>
                  <li>
                    <strong>Pembelian Internasional:</strong> Ketahui total biaya sebelum membeli
                  </li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>
                  Untuk Perusahaan dan Institusi:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>Perencanaan Keuangan:</strong> Hitung biaya gateway pembayaran tahunan
                  </li>
                  <li>
                    <strong>Laporan Akuntansi:</strong> Dokumentasikan biaya secara akurat untuk keperluan akuntansi dan pajak
                  </li>
                  <li>
                    <strong>Membandingkan Gateway Pembayaran:</strong> Putuskan apakah PayPal yang paling sesuai dengan volume penjualan Anda
                  </li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>Tabel Biaya PayPal Berdasarkan Jenis Transaksi</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  Biaya PayPal bervariasi tergantung jenis transaksi. Tabel berikut menunjukkan biaya utama:
                </p>
                <table className="table bmi-table" style={{ marginTop: "1rem" }}>
                  <thead>
                    <tr>
                      <th className="bmi-header" style={{ textAlign: "center" }}>
                        Jenis Transaksi
                      </th>
                      <th className="bmi-header" style={{ textAlign: "center" }}>
                        Persentase
                      </th>
                      <th className="bmi-header" style={{ textAlign: "center" }}>
                        Biaya Tetap (USD)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ textAlign: "center" }}>Transaksi Komersial Standar</td>
                      <td style={{ textAlign: "center" }}>3.49%</td>
                      <td style={{ textAlign: "center" }}>$0.49</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>PayPal Checkout</td>
                      <td style={{ textAlign: "center" }}>2.99%</td>
                      <td style={{ textAlign: "center" }}>$0.49</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>Pembayaran Kecil</td>
                      <td style={{ textAlign: "center" }}>5.00%</td>
                      <td style={{ textAlign: "center" }}>$0.05</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>Teman & Keluarga (Domestik)</td>
                      <td style={{ textAlign: "center" }}>0%</td>
                      <td style={{ textAlign: "center" }}>$0.00</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>Transfer Internasional</td>
                      <td style={{ textAlign: "center" }}>4.50%</td>
                      <td style={{ textAlign: "center" }}>$0.49</td>
                    </tr>
                  </tbody>
                </table>
                <div
                  className="alert alert-success mt-3"
                  style={{ lineHeight: "1.8" }}
                >
                  <strong>✓ Tips:</strong> Untuk jumlah yang sangat kecil (kurang dari 10 dolar), coba jenis "Pembayaran Kecil" karena biaya tetapnya jauh lebih rendah ($0.05 bukan $0.49).
                </div>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>Contoh Praktis Menghitung Biaya PayPal</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  Contoh 1: Freelancer Menerima 500 Dolar
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Kasus:</strong> Desainer grafis ingin menerima 500 dolar untuk sebuah proyek.
                </p>
                <div className="alert alert-info" style={{ fontSize: "14px" }}>
                  <strong>Perhitungan (Transaksi Komersial Standar):</strong><br />
                  Biaya = (500 × 3.49%) + $0.49 = $17.45 + $0.49 = $17.94<br />
                  Jumlah bersih = $500 - $17.94 = <strong>$482.06</strong><br />
                  Agar $500 diterima utuh, klien harus mengirim: <strong>$518.60</strong>
                </div>

                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>
                  Contoh 2: Mengirim 200 Euro ke Teman di Jerman
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Kasus:</strong> Anda ingin mengirim 200 euro sebagai hadiah ke teman di Eropa.
                </p>
                <div className="alert alert-info" style={{ fontSize: "14px" }}>
                  <strong>Perhitungan (Transfer Internasional):</strong><br />
                  Biaya = (200 × 4.5%) + €0.39 = €9.00 + €0.39 = €9.39<br />
                  Yang diterima teman: €200 - €9.39 = <strong>€190.61</strong>
                </div>

                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>
                  Contoh 3: Penjual Toko Online Menerima 1000 Riyal
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Kasus:</strong> Penjual Saudi menerima pembayaran 1000 riyal melalui PayPal.
                </p>
                <div className="alert alert-info" style={{ fontSize: "14px" }}>
                  <strong>Perhitungan (Transaksi Komersial Standar):</strong><br />
                  Biaya = (1000 × 3.49%) + 1.84 riyal = 34.90 + 1.84 = <strong>36.74 riyal</strong><br />
                  Jumlah bersih = <strong>963.26 riyal</strong>
                </div>

                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>
                  Contoh 4: Pembayaran Kecil Senilai 5 Dolar
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Kasus:</strong> Perbandingan biaya transaksi kecil.
                </p>
                <div className="alert alert-info" style={{ fontSize: "14px" }}>
                  <strong>Transaksi Biasa:</strong> (5 × 3.49%) + $0.49 = $0.174 + $0.49 = $0.664<br />
                  <strong>Pembayaran Kecil:</strong> (5 × 5%) + $0.05 = $0.25 + $0.05 = $0.30<br />
                  <strong>Selisih:</strong> Hemat $0.364 per transaksi — signifikan untuk ukuran tersebut!
                </div>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>Tips Mengurangi Biaya PayPal</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  Tips untuk Penjual dan Freelancer:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>Tambahkan Biaya ke Harga:</strong> Jika klien meminta pembayaran melalui PayPal, tambahkan persentase biaya ke harga Anda
                  </li>
                  <li>
                    <strong>Gunakan Checkout untuk Toko:</strong> Persentase 2.99% lebih rendah dari transaksi biasa 3.49%
                  </li>
                  <li>
                    <strong>Gabungkan Pembayaran:</strong> Satu pembayaran besar lebih baik daripada beberapa pembayaran kecil karena biaya tetap dibayar sekali
                  </li>
                  <li>
                    <strong>Jelajahi PayPal Business:</strong> PayPal mungkin memberi Anda biaya yang lebih rendah dengan volume transaksi tinggi
                  </li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>
                  Kesalahan Umum yang Harus Dihindari:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>Jangan minta klien mengirim uang melalui "teman" untuk menghindari biaya — pembeli kehilangan perlindungan pembeli</li>
                  <li>Jangan lupa biaya konversi mata uang (bisa mencapai 3-4% tambahan) untuk transfer internasional</li>
                  <li>Jangan menghitung biaya dari jumlah yang diterima saja — kalkulator menghitung dari jumlah yang dikirim</li>
                  <li>Pastikan jenis transaksi sebelum mengirim jumlah berapa pun untuk menghindari kejutan</li>
                </ul>
                <div
                  className="alert alert-warning mt-3"
                  style={{ lineHeight: "1.8" }}
                >
                  <strong>⚠️ Ingat:</strong> Biaya PayPal berubah dari waktu ke waktu. Selalu pastikan biaya resmi di situs PayPal sebelum transaksi besar.
                </div>
              </div>
            </div>
          </div>
          <br />
          <h2 style={{ fontSize: "20px", marginBottom: "15px" }}>
            Pertanyaan Umum
          </h2>
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>Berapa biaya PayPal untuk 100 dolar?</h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  Untuk transaksi komersial standar, biaya 100 dolar adalah: (100 × 3.49%) + $0.49 = $3.49 + $0.49 = <strong>$3.98</strong>. Penerima menerima $96.02. Jika Anda ingin $100 diterima utuh, Anda harus mengirim sekitar $104.12.
                </p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>Apakah bisa mengirim uang gratis melalui PayPal?</h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  Ya, di dalam negara yang sama Anda dapat mengirim uang gratis melalui opsi "teman dan keluarga" jika sumbernya adalah dompet PayPal atau rekening bank langsung. Namun jika menggunakan kartu kredit, biaya tambahan sekitar 2.9% akan dikenakan. Transfer internasional selalu dikenakan biaya.
                </p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>Apa perbedaan antara transaksi komersial dan pribadi di PayPal?</h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  Transaksi komersial (Goods & Services) digunakan untuk membeli produk atau layanan dan memberikan perlindungan bagi pembeli dan penjual, tetapi dikenakan biaya 3.49%. Transaksi pribadi (Friends & Family) untuk mengirim uang kepada orang terdekat dan gratis secara domestik, tetapi tidak memberikan perlindungan apa pun jika terjadi sengketa.
                </p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>Apakah PayPal memotong biaya tambahan saat mengonversi mata uang?</h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  Ya, PayPal menambahkan margin pada nilai tukar biasanya antara 3% dan 4% di atas nilai resmi. Biaya ini terpisah dari biaya transaksi. Untuk menghindarinya, Anda dapat mengirim dengan mata uang yang sama dengan penerima jika memungkinkan, atau menggunakan layanan seperti Wise untuk transfer internasional dengan biaya lebih rendah.
                </p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>Bagaimana cara menghitung jumlah yang harus saya minta agar penerima menerima jumlah tertentu?</h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  Gunakan opsi "Saya ingin penerima menerima jumlah tertentu" di kalkulator kami. Misalnya, jika nilai layanan Anda 200 dolar dan Anda ingin menerimanya utuh, masukkan 200 dan kalkulator akan memberi tahu bahwa klien harus mengirim sekitar $207.89 untuk transaksi komersial standar.
                </p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>Berapa biaya PayPal untuk toko online?</h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  Toko online yang menggunakan PayPal Checkout membayar 2.99% + biaya tetap, lebih rendah dari biaya standar (3.49%). Beberapa toko dengan volume besar mungkin mendapatkan harga khusus setelah bernegosiasi langsung dengan PayPal.
                </p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>Apakah biaya PayPal untuk riyal Saudi dan dirham UAE sama?</h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  Persentasenya sama (3.49% untuk transaksi standar), tetapi biaya tetap berbeda: untuk riyal Saudi 1.84 riyal dan untuk dirham UAE 1.80 dirham. Kalkulator kami menggunakan nilai aktual ini untuk memastikan akurasi hasil dengan setiap mata uang.
                </p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>Apakah PayPal aman untuk transaksi komersial besar?</h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  PayPal memberikan perlindungan yang baik untuk pembeli dan penjual dalam transaksi komersial, tetapi untuk jumlah yang sangat besar mungkin ada batasan transfer yang perlu diverifikasi. Juga, PayPal dapat membekukan akun baru dengan transaksi besar yang tiba-tiba. Selalu periksa kebijakan PayPal khusus negara Anda sebelum bertransaksi dengan jumlah besar.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PaypalCalculator;